// ── Enrichment provider catalog ───────────────────────────────────────────
// One declarative entry per data provider: capabilities, auth, endpoints, the
// raw fields it returns, and how each maps to OUR canonical schema (company
// attributes — see `updateCompanyProfileRequestSchema` in companies.ts — or
// person identity traits). This is the maintainable integration reference for
// current + future providers: adding/auditing a provider = editing one entry.
//
// `verified: true` means the contract was confirmed against the vendor's docs;
// `false` means it's an integration seam to confirm before enabling (never
// treat an unverified row as fact). `fieldMap[].to: null` flags a provider
// field we DON'T yet have a canonical slot for — a candidate to add.
//
// NB: this is documentation/contract today; the per-provider `fieldMap` is
// shaped so a normalizer can later be driven from it (raw → canonical) instead
// of hand-mapping in each provider.
export const ENRICHMENT_PROVIDERS = [
    {
        id: 'internal',
        name: 'Internal',
        description: 'PDL Free Company Dataset seed + team curation',
        capabilities: ['company'],
        verified: true,
        docs: 'docs/ENRICHMENT.md',
        baseUrl: null, // local DB table `company_enrichment_source`
        auth: 'none (local lookup)',
        endpoints: [
            {
                capability: 'company',
                method: 'GET',
                path: 'company_enrichment_source (DB, by apex domain)',
                input: 'domain',
                noMatch: 'no row → null (no charge)',
                status: 'wired',
            },
        ],
        // PDL fields → canonical company attributes (mostly identity mapping).
        fieldMap: [
            { from: 'name', to: 'name', type: 'string' },
            { from: 'industry', to: 'industry', type: 'string' },
            {
                from: 'size',
                to: 'size',
                type: 'band',
                notes: 'PDL bucket == EMPLOYEE_BANDS',
            },
            { from: 'founded', to: 'founded', type: 'string' },
            { from: 'country', to: 'country', type: 'string' },
            { from: 'region', to: 'region', type: 'string' },
            { from: 'locality', to: 'locality', type: 'string', notes: 'city' },
            { from: 'linkedin_url', to: 'linkedin_url', type: 'string' },
            { from: 'twitter_url', to: 'twitter_url', type: 'string' },
            {
                from: 'website',
                to: 'website',
                type: 'string',
                notes: 'apex becomes the row key',
            },
        ],
        costBasis: '~$0 (no live call); seeded quarterly from the PDL CC BY 4.0 set',
    },
    {
        id: 'delivr',
        name: 'Delivr.ai',
        description: 'Person enrichment from an email the customer already holds',
        capabilities: ['person', 'intent'],
        verified: true,
        docs: 'https://docs.delivr.ai/reference',
        baseUrl: 'https://api.delivr.ai',
        auth: 'X-Api-Key: dlvr_… + X-Api-Secret: … (both required)',
        endpoints: [
            {
                capability: 'person',
                method: 'GET',
                path: '/api/v1/lookup?email=',
                input: 'email (or hem + hem_type)',
                noMatch: '404 (no charge)',
                status: 'wired',
            },
            {
                capability: 'intent',
                method: 'GET',
                path: '/api/v1/intent/company/{domain}, /api/v1/intent/{identifier}',
                input: 'domain / identifier / topic',
                noMatch: '404',
                status: 'future',
            },
        ],
        // Person lookup → identity traits (person slot, not company attributes).
        fieldMap: [
            {
                from: 'first_name',
                to: 'name',
                type: 'string',
                notes: 'name = first + last',
            },
            { from: 'last_name', to: 'name', type: 'string' },
            { from: 'current_business_email', to: 'email', type: 'string' },
            { from: 'business_emails', to: 'emails', type: 'string[]' },
            { from: 'company_name', to: 'company', type: 'string' },
            {
                from: 'is_email_business',
                to: null,
                type: 'string',
                notes: 'flag — no trait slot yet',
            },
        ],
        costBasis: '1 contact record per matched lookup (200); 404 free. DELIVR_PERSON_COST_USD is the contracted Contact Records rate at its first volume bracket. The anonymous pixel path bills the separate resolution_* meter.',
        featureFlag: 'delivr',
        // The wired email lookup only. Anonymous-visitor reveal was parked.
        slots: ['person_enrichment'],
        config: [
            {
                key: 'apiKey',
                label: 'API Key',
                kind: 'secret',
                required: true,
                help: 'X-Api-Key (dlvr_…)',
            },
            {
                key: 'apiSecret',
                label: 'API Secret',
                kind: 'secret',
                required: true,
                help: 'X-Api-Secret (paired with the key)',
            },
            {
                key: 'baseUrl',
                label: 'Base URL',
                kind: 'url',
                default: 'https://api.delivr.ai',
            },
        ],
    },
    {
        id: 'ipapi_is',
        name: 'ipapi.is',
        description: 'Reverse-IP company identification (public, PAYG)',
        capabilities: ['visitor'],
        verified: true, // endpoint + response shape verified (ipapi.is/developers.html)
        docs: 'https://ipapi.is/developers.html',
        baseUrl: 'https://api.ipapi.is',
        auth: 'key query param (?key=)',
        endpoints: [
            {
                capability: 'visitor',
                method: 'GET',
                path: '?q=<ip>&key=',
                input: 'ip',
                noMatch: 'no company.domain in the response',
                status: 'wired',
            },
        ],
        fieldMap: [
            { from: 'company.name', to: 'name', type: 'string' },
            { from: 'company.domain', to: 'domain', type: 'string' },
            {
                from: 'company.type',
                to: 'type',
                type: 'string',
                notes: 'business|isp|hosting|education|government|banking',
            },
        ],
        costBasis: 'Per-lookup PAYG (~$0.0001–0.0002). $/lookup TBD from portal — IPAPI_IS cost 0 until confirmed.',
        // No featureFlag: gated by the `visitorIdCompany` capability + credential presence.
        slots: ['visitor_company'],
        config: [
            {
                key: 'apiKey',
                label: 'API Key',
                kind: 'secret',
                required: true,
                help: 'ipapi.is API key (passed as ?key=)',
            },
            {
                key: 'baseUrl',
                label: 'Base URL',
                kind: 'url',
                default: 'https://api.ipapi.is',
            },
        ],
    },
    {
        id: 'ipregistry',
        name: 'Ipregistry',
        description: 'Reverse-IP company identification (public, prepaid PAYG)',
        capabilities: ['visitor'],
        verified: true, // endpoint + response shape verified (ipregistry.co/docs)
        docs: 'https://ipregistry.co/docs/responses/ip-address-fields',
        baseUrl: 'https://api.ipregistry.co',
        auth: 'key query param (?key=)',
        endpoints: [
            {
                capability: 'visitor',
                method: 'GET',
                path: '/<ip>?key=',
                input: 'ip',
                noMatch: 'no company.domain in the response',
                status: 'wired',
            },
        ],
        fieldMap: [
            { from: 'company.name', to: 'name', type: 'string' },
            { from: 'company.domain', to: 'domain', type: 'string' },
            {
                from: 'company.type',
                to: 'type',
                type: 'string',
                notes: 'business|education|government|hosting|isp',
            },
        ],
        costBasis: 'Prepaid credits (~$0.0001/lookup at 1M). $/lookup TBD from portal — IPREGISTRY cost 0 until confirmed.',
        slots: ['visitor_company'],
        config: [
            {
                key: 'apiKey',
                label: 'API Key',
                kind: 'secret',
                required: true,
                help: 'ipregistry API key (passed as ?key=)',
            },
            {
                key: 'baseUrl',
                label: 'Base URL',
                kind: 'url',
                default: 'https://api.ipregistry.co',
            },
        ],
    },
    {
        id: 'datalegion',
        name: 'Data Legion',
        description: 'Company + person enrichment REST',
        capabilities: ['company', 'person'],
        verified: false, // ⚠ request/response shape is an unverified seam
        docs: 'https://www.datalegion.ai/docs',
        baseUrl: null, // per-slot endpoints via DATALEGION_*_ENDPOINT env
        auth: 'Authorization: Bearer <DATALEGION_API_KEY>  [VERIFY]',
        endpoints: [
            {
                capability: 'company',
                method: 'GET',
                path: '<DATALEGION_COMPANY_ENDPOINT>?domain=  [VERIFY]',
                input: 'domain',
                noMatch: '404 (assumed) [VERIFY]',
                status: 'wired',
            },
            {
                capability: 'person',
                method: 'GET',
                path: '<DATALEGION_PERSON_ENDPOINT>?email=  [VERIFY]',
                input: 'email',
                noMatch: '404 (assumed) [VERIFY]',
                status: 'wired',
            },
        ],
        fieldMap: [], // [VERIFY] — fields unknown until the contract is confirmed
        costBasis: 'company $0.025/record, person $0.05/record [sourced: datalegion.ai/pricing]',
        featureFlag: 'datalegion',
        slots: ['company_enrichment', 'person_enrichment'],
        config: [
            {
                key: 'apiKey',
                label: 'API Key',
                kind: 'secret',
                required: true,
                help: 'Authorization: Bearer <key>',
            },
            {
                key: 'companyEndpoint',
                label: 'Company Endpoint',
                kind: 'url',
                required: true,
                slot: 'company_enrichment',
                help: 'Full URL; ?domain= is appended',
            },
            {
                key: 'personEndpoint',
                label: 'Person Endpoint',
                kind: 'url',
                required: true,
                slot: 'person_enrichment',
                help: 'Full URL; ?email= is appended',
            },
        ],
    },
    {
        id: 'cleanlist',
        name: 'Cleanlist',
        description: 'Company enrichment REST, synchronous',
        capabilities: ['company'],
        verified: true,
        docs: 'https://docs.cleanlist.ai/api-reference/enrichment',
        baseUrl: 'https://api.cleanlist.ai',
        auth: 'Authorization: Bearer clapi_…',
        endpoints: [
            {
                capability: 'company',
                method: 'POST',
                path: '/api/v1/public/enrichment/company',
                input: 'domain (body)',
                noMatch: 'empty record / 404 → no charge; 402 = out of credits (hard error)',
                status: 'wired',
            },
        ],
        fieldMap: [
            { from: 'name', to: 'name', type: 'string' },
            { from: 'industry', to: 'industry', type: 'string' },
            {
                from: 'industries',
                to: null,
                type: 'string[]',
                notes: 'candidate canonical: industries[]',
            },
            { from: 'employee_count_range', to: 'size', type: 'band' },
            {
                from: 'revenue_range',
                to: null,
                type: 'band',
                notes: 'candidate canonical: revenue band',
            },
            {
                from: 'hq_location',
                to: 'locality',
                type: 'string',
                notes: 'split into country/region/locality [VERIFY]',
            },
            { from: 'funding_stage', to: 'funding_stage', type: 'string' },
            {
                from: 'total_funding_usd',
                to: null,
                type: 'number',
                notes: 'candidate canonical: total_funding',
            },
            { from: 'tech_stack', to: 'tech_stack', type: 'string[]' },
            { from: 'linkedin_url', to: 'linkedin_url', type: 'string' },
            { from: 'twitter_url', to: 'twitter_url', type: 'string' },
        ],
        costBasis: '~$0.05/company (1 Cleanlist credit) [your estimate — confirm from contract]',
        featureFlag: 'cleanlist',
        slots: ['company_enrichment'],
        config: [
            {
                key: 'apiKey',
                label: 'API Key',
                kind: 'secret',
                required: true,
                help: 'Authorization: Bearer clapi_…',
            },
            {
                key: 'baseUrl',
                label: 'Base URL',
                kind: 'url',
                default: 'https://api.cleanlist.ai',
            },
        ],
    },
    // ── Web search providers (research agents' outbound search waterfall) ──────
    // (Amazon Bedrock AgentCore Web Search is a planned addition — see
    // .ai/plans/agentcore-web-search/.)
    {
        id: 'brave',
        name: 'Brave Search',
        description: 'Independent web-search index API',
        // Contract confirmed 2026-06-27 against Brave's official API reference +
        // Brave's own MCP server types (see web-search-brave.ts header).
        capabilities: ['web_search'],
        verified: true,
        docs: 'https://api-dashboard.search.brave.com/api-reference/web/search/get',
        baseUrl: 'https://api.search.brave.com',
        auth: 'X-Subscription-Token header',
        endpoints: [
            {
                capability: 'web_search',
                method: 'GET',
                path: '/res/v1/web/search?q=',
                input: 'query',
                noMatch: 'empty web.results',
                status: 'planned',
            },
        ],
        fieldMap: [],
        costBasis: 'Brave Search API per-query — confirm plan/pricing',
        featureFlag: 'brave',
        slots: ['web_search'],
        config: [
            {
                key: 'apiKey',
                label: 'API Key',
                kind: 'secret',
                required: true,
                help: 'X-Subscription-Token',
            },
            {
                key: 'baseUrl',
                label: 'Base URL',
                kind: 'url',
                default: 'https://api.search.brave.com',
            },
        ],
    },
    {
        id: 'tavily',
        name: 'Tavily Search',
        description: 'Web search API built for AI agents',
        // Contract confirmed 2026-06-28 against Tavily's official API reference
        // (see web-search-tavily.ts header).
        capabilities: ['web_search'],
        verified: true,
        docs: 'https://docs.tavily.com/documentation/api-reference/endpoint/search',
        baseUrl: 'https://api.tavily.com',
        auth: 'Authorization: Bearer header',
        endpoints: [
            {
                capability: 'web_search',
                method: 'POST',
                path: '/search',
                input: 'query',
                noMatch: 'empty results',
                status: 'planned',
            },
        ],
        fieldMap: [],
        costBasis: 'Tavily Search API per-query — confirm plan/pricing',
        featureFlag: 'tavily',
        slots: ['web_search'],
        config: [
            {
                key: 'apiKey',
                label: 'API Key',
                kind: 'secret',
                required: true,
                help: 'Authorization: Bearer tvly-…',
            },
            {
                key: 'baseUrl',
                label: 'Base URL',
                kind: 'url',
                default: 'https://api.tavily.com',
            },
        ],
    },
    {
        id: 'dataforseo',
        name: 'DataForSEO',
        description: 'Structured SERP + SEO data API (primary web-search provider)',
        // Contract confirmed 2026-07-10 against DataForSEO's official docs:
        //   • auth: https://docs.dataforseo.com/v3/auth/ (HTTP Basic, base64(login:password))
        //   • SERP: https://docs.dataforseo.com/v3/serp/google/organic/live/advanced/
        // See web-search-dataforseo.ts header for the full response mapping.
        capabilities: ['web_search'],
        verified: true,
        docs: 'https://docs.dataforseo.com/v3/serp/google/organic/live/advanced/',
        baseUrl: 'https://api.dataforseo.com',
        auth: 'Authorization: Basic base64(login:password)',
        endpoints: [
            {
                capability: 'web_search',
                method: 'POST',
                path: '/v3/serp/google/organic/live/advanced',
                input: 'query',
                noMatch: 'empty tasks[].result[].items',
                status: 'planned',
            },
        ],
        fieldMap: [],
        costBasis: 'DataForSEO Live Advanced SERP $0.002/query (Standard queue $0.0006) — pay-as-you-go',
        featureFlag: 'dataforseo',
        slots: ['web_search'],
        // Basic Auth needs TWO fields: the account API login (an identifier — plaintext
        // `text`) and the auto-generated API password (secret → vault). Both required.
        config: [
            {
                key: 'login',
                label: 'API Login',
                kind: 'text',
                required: true,
                help: 'DataForSEO account API login (email), from the API Access tab',
            },
            {
                key: 'password',
                label: 'API Password',
                kind: 'secret',
                required: true,
                help: 'Auto-generated API password (API Access tab) — NOT your account password',
            },
            {
                key: 'baseUrl',
                label: 'Base URL',
                kind: 'url',
                default: 'https://api.dataforseo.com',
            },
        ],
    },
    {
        id: 'web_research',
        name: 'Web Research',
        description: 'Web search + extraction model — the last-resort gap-filler when data providers no-match',
        capabilities: ['company'],
        // Internal composition (our own search waterfall + Bedrock extraction) —
        // there is no external contract to verify.
        verified: true,
        docs: 'docs/ENRICHMENT.md',
        baseUrl: null,
        auth: 'none (internal composition: web_search slot + Bedrock)',
        endpoints: [
            {
                capability: 'company',
                method: 'GET',
                path: 'WebSearchService.search → Bedrock extraction (in-process)',
                input: 'domain',
                noMatch: 'no search results, or extraction yields no core field → null (no charge)',
                status: 'wired',
            },
        ],
        // The extraction contract: the model may emit ONLY these flat fields, all
        // of which normalizeAttributes already canonicalizes.
        fieldMap: [
            { from: 'name', to: 'name', type: 'string' },
            { from: 'description', to: 'description', type: 'string' },
            { from: 'industry', to: 'industry', type: 'string' },
            {
                from: 'employee_count',
                to: 'size',
                type: 'number',
                notes: 'via normalizeAttributes EMPLOYEE_BANDS',
            },
            {
                from: 'hq_location',
                to: 'locality',
                type: 'string',
                notes: 'normalizer splits into locality/region/country',
            },
            { from: 'founded', to: 'founded', type: 'string' },
            { from: 'company_type', to: 'company_type', type: 'string' },
            { from: 'funding_stage', to: 'funding_stage', type: 'string' },
            { from: 'revenue_range', to: 'revenue_range', type: 'string' },
            { from: 'linkedin_url', to: 'linkedin_url', type: 'string' },
        ],
        costBasis: 'extraction-model tokens (computed per run from MODEL_USD_RATES) + one web-search query (search per-query cost not modeled yet)',
        featureFlag: 'webResearch',
        slots: ['company_enrichment'],
        dependsOnSlot: 'web_search',
    },
];
/** Lookup a provider spec by id. */
export const getEnrichmentProvider = (id) => ENRICHMENT_PROVIDERS.find((p) => p.id === id);
/** Providers that expose runtime config (credentials/endpoints) — i.e. the ones
 *  the super-admin Providers UI renders a credential form for. */
export const CREDENTIAL_PROVIDERS = ENRICHMENT_PROVIDERS.filter((p) => (p.config?.length ?? 0) > 0);
//# sourceMappingURL=enrichment-catalog.js.map